/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file IndiBloodPressureMonitoringView.jsx
 * @author Andres Solarte
 * @description IndiBloodPressureMonitoring View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardTitle from 'components/Charts/DashboardTitle';
import ProgressBar from 'components/Charts/ProgressBar';
import { useHistory } from 'react-router';
import styleGuideColors from 'commons/styleGuideColors';
import EmptyChart from 'components/EmptyChart';
import { Spin } from 'antd';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * Indi Blood Pressure Monitoring View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function IndiBloodPressureMonitoringView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getIndiBloodPressureMonitoring } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    const data = [];
    const colors = {
      'No Alerta': styleGuideColors.baxterBrandTeal,
      'Sin Registro': styleGuideColors.graphYellow,
      Alerta: styleGuideColors.alertError
    };
    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });
      resp.data.forEach(({ cantidad, clasificacion }) => {
        const percent = (cantidad * 100) / total;
        const item = {
          title: cantidad,
          subtitle:
            clasificacion === 'No Alerta' ? 'Sin Alerta' : clasificacion,
          percent: useTruncateAndFormat(percent, 2),
          color: colors[clasificacion]
        };
        if (clasificacion === 'No Alerta') {
          data[1] = item;
        } else if (clasificacion === 'Sin Registro') {
          data[0] = item;
        } else if (clasificacion === 'Alerta') {
          data[2] = item;
        }
      });
      setChartData(data);
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    setChartData(null);
    getIndiBloodPressureMonitoring({ clinicId }, onGetData);
  }, [clinicId, translate]);

  const handleDetailClick = (index) => {
    // Get data by index and send by url param to detail list
    const { title: totalItems, subtitle } = chartData[index];
    const classification = subtitle === 'Sin Alerta' ? 'No Alerta' : subtitle;
    history.push(
      `/main/dashboard-detail/ambulatory-monitoring-ib/${classification}/${totalItems}`
    );
  };

  return chartData ? (
    <div className="flex flex-wrap">
      <DashboardTitle
        title={translate('dashboardStaff.ambulatoryMonitoring')}
      />
      <div className="px-2 w-full">
        {chartData?.length === 0 && <EmptyChart />}
        {chartData?.map(({ color, title, subtitle, percent }, index) => (
          <div onClick={() => handleDetailClick(index)} key={index}>
            <ProgressBar
              color={color}
              title={title}
              subtitle={subtitle}
              percent={percent}
            />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <Spin />
  );
}

/**
 * Component definition of proptypes.
 */
IndiBloodPressureMonitoringView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getIndiBloodPressureMonitoring: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default IndiBloodPressureMonitoringView;
